
import EventTreeMxGraph from '../HCLTreeMxGraph/EventTreeMxGraph';
import InitVertex from '../HCLTreeNode/EventTreeNode/InitEvent/InitVertex';
import EndStateVertex from '../HCLTreeNode/EventTreeNode/EndStateNode/EndStateVertex';
import EventTreeVertex from "../HCLTreeNode/EventTreeNode/EventTreeVertex";
import FunctionalEventVertex from "../HCLTreeNode/EventTreeNode/FunctionalEventNode/FunctionalEventVertex";
import {mxGraphLayout} from "mxgraph-js";

class EventTreeMxGraphLayout extends mxGraphLayout {
  private visited: {[key: string]: boolean};
  private lanes: EventTreeVertex[];
  private xMax: number;
  private yMax: number;
  private static OFFSET_X = 350;
  private static OFFSET_Y = 100;
  private static SKIP_OFFSET_X = 180;
  private static SKIP_OFFSET_Y = 150;

  constructor(graph: EventTreeMxGraph) {
    super(graph);
  }

  execute(parent: any) {
    this.visited = {};
    this.lanes = [];
    this.xMax = 0;
    this.yMax = 0;

    const graph = this.getGraph() as EventTreeMxGraph;
    const vertices = graph.getVertices();

    let root: InitVertex;
    const endStates: EndStateVertex[] = [];

    // reset positions
    vertices.forEach(vertex => {
      if (vertex instanceof InitVertex) {
        root = vertex as InitVertex;
      } else if (vertex instanceof EndStateVertex) {
        endStates.push(vertex as EndStateVertex);
      }
      const cell = this.getVertexBounds(vertex);
      this.getGraph().moveCells([vertex], -cell.x, -cell.y);
    });

    this.lanes = endStates.map((e, idx) => null);

    this.dfs(root, EventTreeMxGraphLayout.OFFSET_X, EventTreeMxGraphLayout.OFFSET_Y, EventTreeMxGraphLayout.SKIP_OFFSET_X, EventTreeMxGraphLayout.SKIP_OFFSET_Y);
    endStates.forEach((es, idx) => this.getGraph().moveCells([es], this.xMax, 0));
  }

  private dfs(root: EventTreeVertex, x: number, y: number, xSkip: number, ySkip: number, isFromSuccessEdge = false) {
    if (this.visited[root.id]) return;
    if (this.xMax < x) this.xMax = x;

    if (!isFromSuccessEdge && this.yMax > y) y = this.yMax + ySkip;

    let needLongerEdge = !isFromSuccessEdge;
    if (this.yMax < y) {
      this.yMax = y;
      needLongerEdge = false;
    }

    this.visited[root.id] = true;

    if (root instanceof EndStateVertex) {
      const idx = Math.floor((y - ySkip) / ySkip);
      if (!this.lanes[idx]) {
        this.getGraph().moveCells([root], 0, y);
        this.lanes[idx] = root;
        return;
      }

      const emptyIdx = this.lanes.indexOf(null);

      if (emptyIdx === -1) return;

      const newY = EventTreeMxGraphLayout.OFFSET_Y + ySkip * (emptyIdx + 1);
      if (emptyIdx > -1) {
        this.getGraph().moveCells([root], 0, newY);
        this.yMax = (this.yMax < newY) ? newY : this.yMax;
        this.lanes[emptyIdx] = root;
        return;
      }
      return;
    }

    const successChildren: EventTreeVertex[] = [];
    const failureChildren: EventTreeVertex[] = [];
    const otherChildren: EventTreeVertex[] = [];

    if (root instanceof FunctionalEventVertex) {

      if (root.successPort.edges) {
        root.successPort.edges.forEach(edge => {
          successChildren.push(edge.target as EventTreeVertex);
        });
      }

      if (root.failurePort.edges) {
        root.failurePort.edges.forEach(edge => {
          failureChildren.push(edge.target as EventTreeVertex);
        });
      }

    } else {

      if (root.edges) {
        root.edges.forEach(edge => {
          otherChildren.push(edge.target as EventTreeVertex);
        });
      }

    }

    const newY = needLongerEdge ? y + ySkip : y;
    this.getGraph().moveCells([root], x, newY);

    successChildren.forEach(c => this.dfs(c, x + xSkip, newY, xSkip, ySkip, true));
    failureChildren.forEach(c => this.dfs(c, x + xSkip, newY + ySkip, xSkip, ySkip, false));
    otherChildren.forEach(c => this.dfs(c, x, newY + ySkip, xSkip, ySkip));

  }

}

export default EventTreeMxGraphLayout;
